import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Button, Checkbox, CircularProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Select, MenuItem, TextField } from '@mui/material';
import { getPlayersThirdPartyById, fetchTeams, fetchCompetitions } from "../../../../api/index.js";
import useFetch from '../../../../v2/hooks/useFetch.js';
import avatar from '../../../../images/avatar-anonyme.png';
import DatePicker_Dsas from '../../../../dsasComponents/DatePicker_Dsas.js';
import SelectGroupProvider from '../../../../dsasComponents/SelectGroupProvider.js';
import { discipline_all } from '../../../../bdd/bddSTUB.js';
import { updatePlayer } from '../../../../actions/players.js';
import { useDispatch } from 'react-redux';
import ImageHandlerTool from '../../../components/ImageHandlerTool.js';

const EditPlayers = () => {
  const { thirdPartyId, thirdPartyEntityId } = useParams();
  const { data, isLoading, error, fetchData } = useFetch(getPlayersThirdPartyById, [thirdPartyId, thirdPartyEntityId]);
  const [playersData, setPlayersData] = useState([]);
  const [teamsList, setTeamsList] = useState([]);
  const [competitions, setCompetitions] = useState([]);
  const [modifiedPlayers, setModifiedPlayers] = useState({});
  const [selectedFields, setSelectedFields] = useState({});
  const [selectedPlayers, setSelectedPlayers] = useState({});
  const dispatch = useDispatch();

  useEffect(() => {
    if (data) setPlayersData(data);
  }, [data]);

  useEffect(() => {
    if (!thirdPartyId || !thirdPartyEntityId) return;
    fetchData();
  }, [thirdPartyId, thirdPartyEntityId]);

  useEffect(() => {
    const loadTeamsAndCompetitions = async () => {
      const teamsResponse = await fetchTeams();
      setTeamsList(teamsResponse.data || []);
      const competitionsResponse = await fetchCompetitions();
      setCompetitions(competitionsResponse.data || []);
    };
    loadTeamsAndCompetitions();
  }, []);

  const handleChange = (playerId, key, value) => {
    setPlayersData((prev) =>
      prev.map((player) => {
        if (selectedPlayers[player._id] && selectedFields[key]) {
          return { ...player, [key]: value };
        }
        return player;
      })
    );

    setModifiedPlayers((prev) => {
      const updatedModifiedPlayers = { ...prev };
      playersData.forEach((player) => {
        if (selectedPlayers[player._id] && selectedFields[key]) {
          updatedModifiedPlayers[player._id] = true;
        }
      });
      return updatedModifiedPlayers;
    });
  };

  const handleImageChange = (playerId, file) => {

    const reader = new FileReader();
    reader.readAsDataURL(file);


    reader.onloadend = () => {

      const base64data = reader.result;

        setPlayersData((prev) =>
          prev.map((player) => {
            if (selectedPlayers[player._id] && selectedFields['photoUrl']) {
              return { ...player, photo: file, photoUrl: base64data};
            }
            return player;
          })
        );

        setModifiedPlayers((prev) => {
          const updatedModifiedPlayers = { ...prev };
          playersData.forEach((player) => {
            if (selectedPlayers[player._id] && selectedFields['photoUrl']) {
              updatedModifiedPlayers[player._id] = true;
            }
          });
          return updatedModifiedPlayers;
        });

    };
      
  };

  const handleSaveChanges = async (playerId) => {
    const playerData = playersData.find((player) => player._id === playerId);
    const sanitizedPlayerData = { ...playerData };

    ['number', 'size', 'weight', 'birthday'].forEach((key) => {
      if (!sanitizedPlayerData[key]) delete sanitizedPlayerData[key];
    });

    // Refactoriser teamsSeason
    sanitizedPlayerData.teamsSeason = sanitizedPlayerData.teamsSeason.map((t) => ({
      team: t.team._id,
      startDate: t.startDate,
      endDate: t.endDate,
    }));

    dispatch(updatePlayer(sanitizedPlayerData._id, sanitizedPlayerData));
    
    setModifiedPlayers((prev) => ({ ...prev, [playerId]: false }));
  };

  const handleFieldCheckboxChange = (fieldKey) => {
    setSelectedFields((prev) => ({ ...prev, [fieldKey]: !prev[fieldKey] }));
  };

  const handlePlayerCheckboxChange = (playerId) => {
    setSelectedPlayers((prev) => ({ ...prev, [playerId]: !prev[playerId] }));
  };

  const fields = [
    { label: "Photo", key: "photoUrl", type: "image", component: Box },
    { label: "Groupe", key: "provider", type: "select", component: SelectGroupProvider },
    { label: "Nom", key: "lastname", type: "text", component: TextField },
    { label: "Prénom", key: "firstname", type: "text", component: TextField },
    { label: "Discipline", key: "discipline", type: "select", component: Select, options: discipline_all },
    { label: "Numéro", key: "number", type: "text", component: TextField },
    { label: "Date de naissance", key: "birthday", type: "date", component: DatePicker_Dsas },
    { label: "Nationalité", key: "nationality", type: "text", component: TextField },
    { label: "Taille (cm)", key: "size", type: "text", component: TextField },
    { label: "Poids (kg)", key: "weight", type: "text", component: TextField },
    { label: "Position", key: "positioning", type: "text", component: TextField },
    { label: "Id externe", key: "thirdPartyEntityId", type: "text", component: TextField },
    { label: "Origine Id externe", key: "thirdPartyId", type: "text", component: TextField },
  ];

  return (
    <TableContainer component={Paper} sx={{ m: 2 }}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>Id</TableCell>
            {playersData.map((player) => (
              <TableCell key={player._id} align="center">
                <Checkbox
                  checked={!!selectedPlayers[player._id]}
                  onChange={() => handlePlayerCheckboxChange(player._id)}
                  color="primary"
                />
                <a href={`/sports/players/create/${player._id}`} target="_blank" rel="noopener noreferrer">
                  {player._id}
                </a>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
            {playersData.map((player) => (
              <TableCell key={`action-${player._id}`} align="center">
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => handleSaveChanges(player._id)}
                  disabled={!modifiedPlayers[player._id]}
                >
                  Valider les changements
                </Button>
              </TableCell>
            ))}
          </TableRow>
          {fields.map((field) => (
            <TableRow key={field.key}>
              <TableCell component="th" scope="row">
                <Checkbox
                  checked={!!selectedFields[field.key]}
                  onChange={() => handleFieldCheckboxChange(field.key)}
                  color="primary"
                />
                {field.label}
              </TableCell>
              {playersData.map((player) => (
                <TableCell key={`${player._id}-${field.key}`} align="center">
                  {(() => {
                    const Component = field.component;
                    {/*Si isEditable est undefined forcer a false*/}
                    const isEditable = (selectedFields[field.key] && selectedPlayers[player._id]) || false;
                    
                   

                    // console.log('Is editable ? '+isEditable+' : field.key = '+ field.key + ' [selected:'+selectedFields[field.key]+ '] - player._id = ' + player._id + ' [selected:'+selectedPlayers[player._id]+ ']' )
                    
                    

                    if (field.type === 'text') {
                      return (
                        <Component
                          value={player[field.key] || ''}
                          onChange={(e) => handleChange(player._id, field.key, e.target.value)}
                          disabled={!isEditable}
                        />
                      );
                    }

                    if (field.type === 'select') {
                      return (
                        <Component
                          value={player[field.key] || ''}
                          onChange={(e) => handleChange(player._id, field.key, e.target.value)}
                          fullWidth
                          disabled={!isEditable}
                        >
                          {field.options && field.options.map((option) => (
                            <MenuItem key={option._id} value={option._id}>
                              {option.name}
                            </MenuItem>
                          ))}
                        </Component>
                      );
                    }

                    if (field.type === 'date') {
                      return (
                        <Component
                          value={player[field.key]}
                          onChange={(date) => handleChange(player._id, field.key, date)}
                          disabled={!isEditable}
                        />
                      );
                    }

                    if (field.type === 'image') {
                      return <>
                      <ImageHandlerTool 
                      key={player._id} 
                      initialImage={player.photoUrl || avatar} 
                      onImageSave={(image) => handleImageChange(player._id, image)} 
                      isEditable={isEditable} />
                      </>
                    }

                    return null;

                  })()}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default EditPlayers;
